import React, { useEffect, useState, useMemo } from "react";
import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard, Music, CheckCircle2, FileText, Send,
  FolderOpen, ShieldCheck, History, Settings as SettingsIcon,
  LogOut, Search, Bell, Sparkles, Menu, X, Globe2, MessageSquare,
  KeyRound, Library, Inbox, Briefcase, ClipboardList, FileMusic, Mail,
  ChevronDown, ChevronRight, FlaskConical, Radio, ScrollText, Podcast,
  Rocket, LineChart, Database, BrushCleaning, Gavel,
  Fingerprint, Lock, Vault, User as UserIcon,
  BookText,
  Disc3,
} from "lucide-react";
import { canAccessBriefIntelligence, useAuth, roleLabel } from "@/lib/auth";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import BrandLogo from "@/components/BrandLogo";
import BetaNoticeBanner from "@/components/BetaNoticeBanner";
import FeedbackWidget from "@/components/FeedbackWidget";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/tracks", label: "Catalog / Tracks", icon: Music },
  { to: "/ready-tracks", label: "Ready Tracks", icon: CheckCircle2 },
  // Sync Opportunities is restricted to write-capable roles. Beta Tester and
  // any future read-only roles will not see this entry.
  { to: "/sync-opportunities", label: "Sync Opportunities", icon: Briefcase,
    roles: ["super_admin", "gfmp_reviewer", "catalog_manager"] },
  // Consultation Requests (inbound from public Kandake Compliance site) —
  // same writer-role gate as Sync Opportunities.
  { to: "/consultation-requests", label: "Consultation Requests", icon: ClipboardList,
    roles: ["super_admin", "gfmp_reviewer", "catalog_manager"] },
  // Kandake Demo &amp; Development Intake (MP8) — writer-role gated. Surfaces
  // public submissions for triage, screening, holds, and promotion-to-track.
  { to: "/demos", label: "Demo Intake", icon: FileMusic,
    roles: ["super_admin", "gfmp_reviewer", "catalog_manager"] },
  // MP10 — Industry Beta Reviewer feedback inspector (internal-only view).
  { to: "/admin/beta-feedback", label: "Beta Feedback", icon: MessageSquare,
    roles: ["super_admin", "gfmp_reviewer", "catalog_manager"] },
  { to: "/license-requests", label: "License Requests", icon: FileText },
  { to: "/submissions", label: "Submissions", icon: Send },
  { to: "/documents", label: "Documents", icon: FolderOpen },
  // Kandake Document Repository (Locked Spec, Phase 1) — the authoritative
  // vault for GFMP / GFMP Sync / Kingswork documentation.
  { to: "/vault", label: "Document Vault", icon: Vault },
  { to: "/compliance", label: "Compliance Review", icon: ShieldCheck },
  { to: "/ai-review-notes", label: "AI Review Notes", icon: Sparkles },
  { to: "/pro-directory", label: "PRO / CMO Directory", icon: Globe2 },
  { to: "/audit", label: "Audit Log", icon: History },
];

// Admin-only secondary nav. Beta Operations is a lightweight observation tool
// (Corrections 5 + 6), visible only to Super Admin.
const ADMIN_NAV = [
  { to: "/catalog-codes", label: "Catalog Codes", icon: Library, role: "super_admin" },
  // "Bulk Metadata" = existing spreadsheet/metadata intake (behaviour
  // unchanged); "Bulk Audio Intake" opens the same /intake page with the
  // existing audio mode preselected.
  { to: "/intake", label: "Bulk Metadata", icon: Inbox, role: "super_admin" },
  { to: "/intake?mode=audio", label: "Bulk Audio Intake", icon: FileMusic, role: "super_admin" },
  { to: "/admin/beta", label: "Beta Operations", icon: MessageSquare, role: "super_admin" },
];

// MP12 Advanced Features — collapsed left-console container. Items surface
// tools that are not part of ordinary daily operator flow. Kept closed by
// default so the standard console stays uncluttered for non-technical
// operators. Expansion state is remembered per-user in localStorage.
const ADVANCED_FEATURES_NAV = [
  {
    to: "/advanced/communications",
    label: "Communications Hub",
    icon: Radio,
    roles: ["super_admin", "gfmp_reviewer", "catalog_manager"],
  },
  {
    to: "/advanced/proof-ledger",
    label: "Proof Ledger",
    icon: ScrollText,
    roles: ["super_admin", "gfmp_reviewer", "catalog_manager"],
  },
  {
    // MP12 Module 4 — Sync Command Desk. Internal-only. Same RBAC as
    // Communications Hub and Proof Ledger. Never surfaced to beta testers,
    // industry reviewers, public users, or any future external Sync Agent role.
    to: "/advanced/sync-command-desk",
    label: "Sync Command Desk",
    icon: Podcast,
    roles: ["super_admin", "gfmp_reviewer", "catalog_manager"],
  },
  // MP12 Module 5 — Release Intelligence, Market Signals, Metadata Registry,
  // Clean Sweep, and Commercial Exceptions. All INTERNAL-ONLY. Same RBAC as
  // the other Advanced Features entries; never surfaced to external roles.
  {
    to: "/advanced/release-intel",
    label: "Release Intelligence",
    icon: Rocket,
    roles: ["super_admin", "gfmp_reviewer", "catalog_manager"],
  },
  {
    to: "/advanced/market-signals",
    label: "Market Signals",
    icon: LineChart,
    roles: ["super_admin", "gfmp_reviewer", "catalog_manager"],
  },
  {
    to: "/advanced/metadata-registry",
    label: "Metadata Registry",
    icon: Database,
    roles: ["super_admin", "gfmp_reviewer", "catalog_manager"],
  },
  {
    to: "/advanced/clean-sweep",
    label: "Clean Sweep",
    icon: BrushCleaning,
    roles: ["super_admin", "gfmp_reviewer", "catalog_manager"],
  },
  {
    to: "/advanced/commercial-exceptions",
    label: "Commercial Exceptions",
    icon: Gavel,
    roles: ["super_admin", "gfmp_reviewer", "catalog_manager"],
  },
  // MP12 Module 6 — Security Separation. Restricted Identity is limited to
  // super_admin + gfmp_reviewer; Restricted Records + Vault Dockets follow
  // the standard Advanced Features RBAC. All three are internal-only.
  {
    to: "/advanced/restricted-identity",
    label: "Restricted Identity",
    icon: Fingerprint,
    roles: ["super_admin", "gfmp_reviewer"],
  },
  {
    to: "/advanced/restricted-records",
    label: "Restricted Records",
    icon: Lock,
    roles: ["super_admin", "gfmp_reviewer", "catalog_manager"],
  },
  {
    to: "/advanced/vault-dockets",
    label: "Vault Dockets",
    icon: Vault,
    roles: ["super_admin", "gfmp_reviewer", "catalog_manager"],
  },
  // Kandake Iron Gate — AI Provenance & Eligibility.
  {
    to: "/advanced/ai-provenance",
    label: "AI Provenance",
    icon: ShieldCheck,
    roles: ["super_admin", "gfmp_reviewer"],
  },
];

const ADVANCED_FEATURES_STORAGE_KEY = "kandake.nav.advancedFeatures.open";

// GFMP Sync — operational grouping for the sync workflow tools. Entries are
// MOVED here (not duplicated): Brief Intelligence + Catalog Intelligence keep
// their MAGI feature identity, existing routes, descriptions and role gates;
// Email Delivery keeps its Super-Admin-only restriction. The heading renders
// only when the signed-in role can see at least one child entry.
const GFMP_SYNC_NAV = [
  {
    to: "/advanced/briefs",
    label: "Brief Intelligence",
    description: "Structured intake and extraction from client briefs.",
    icon: BookText,
    roles: ["super_admin", "gfmp_reviewer", "catalog_manager", "legal_review"],
    requiresBriefIntelligence: true,
  },
  {
    to: "/advanced/catalog-intelligence",
    label: "Catalog Intelligence",
    description: "Canonical intelligence profiles for catalog titles.",
    icon: Disc3,
    roles: ["super_admin", "gfmp_reviewer", "catalog_manager", "legal_review"],
    requiresBriefIntelligence: true,
  },
  // MP9 — Outbound email configuration + delivery inspector. Super-Admin
  // only so credentials, test sends, and retries remain locked down.
  {
    to: "/admin/email",
    label: "Email Delivery",
    icon: Mail,
    roles: ["super_admin"],
  },
];

const GFMP_SYNC_STORAGE_KEY = "kandake.nav.gfmpSync.open";

// MAGI — AI-powered and AI-assisted intelligence systems. Brief Intelligence
// and Catalog Intelligence retain their MAGI identity but are surfaced under
// the GFMP Sync operational grouping above. MAGI remains a recognised,
// separate feature area: the section machinery below is preserved and the
// heading renders only when an authorised working child entry exists.
// LOCKED FUTURE-PLACEMENT RULE: when Iron Gate 2.0 is implemented under a
// separate authorised assignment, its navigation entry belongs here beneath
// MAGI — not GFMP Sync and not Advanced Features. No placeholder or dead
// route is created in the meantime.
const MAGI_NAV = [];

const MAGI_STORAGE_KEY = "kandake.nav.magi.open";

function useGfmpSyncOpenState() {
  const [open, setOpen] = useState(() => {
    try {
      return localStorage.getItem(GFMP_SYNC_STORAGE_KEY) === "1";
    } catch {
      return false;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem(GFMP_SYNC_STORAGE_KEY, open ? "1" : "0");
    } catch { /* ignore quota errors */ }
  }, [open]);
  return [open, setOpen];
}

function useMagiOpenState() {
  const [open, setOpen] = useState(() => {
    try {
      return localStorage.getItem(MAGI_STORAGE_KEY) === "1";
    } catch {
      return false;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem(MAGI_STORAGE_KEY, open ? "1" : "0");
    } catch { /* ignore quota errors */ }
  }, [open]);
  return [open, setOpen];
}

function useAdvancedFeaturesOpenState() {
  const [open, setOpen] = useState(() => {
    try {
      return localStorage.getItem(ADVANCED_FEATURES_STORAGE_KEY) === "1";
    } catch {
      return false;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem(ADVANCED_FEATURES_STORAGE_KEY, open ? "1" : "0");
    } catch { /* ignore quota errors */ }
  }, [open]);
  return [open, setOpen];
}

function SidebarContent({ user, location, onNavigate, logout }) {
  const navigateTo = useNavigate();
  const [advOpen, setAdvOpen] = useAdvancedFeaturesOpenState();
  const [magiOpen, setMagiOpen] = useMagiOpenState();
  const [gfmpOpen, setGfmpOpen] = useGfmpSyncOpenState();
  const gfmpItems = GFMP_SYNC_NAV.filter(
    (n) =>
      (!n.roles || n.roles.includes(user?.role)) &&
      (!n.requiresBriefIntelligence || canAccessBriefIntelligence(user))
  );
  const anyGfmpActive = gfmpItems.some((n) => location.pathname.startsWith(n.to));
  const magiItems = canAccessBriefIntelligence(user)
    ? MAGI_NAV.filter((n) => !n.roles || n.roles.includes(user?.role))
    : [];
  const anyMagiActive = magiItems.some((n) => location.pathname.startsWith(n.to));
  const advItems = ADVANCED_FEATURES_NAV.filter(
    (n) => !n.roles || n.roles.includes(user?.role)
  );
  const anyAdvActive = advItems.some((n) => location.pathname.startsWith(n.to));
  // Auto-expand on first mount if we already landed on an Advanced route.
  useEffect(() => {
    if (anyGfmpActive && !gfmpOpen) setGfmpOpen(true);
    if (anyMagiActive && !magiOpen) setMagiOpen(true);
    if (anyAdvActive && !advOpen) setAdvOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <>
      <div className="px-5 py-5 flex items-center gap-3">
        <BrandLogo variant="kandake-gold" size="md" testId="shell-kandake-logo" frame="rounded" />
        <div>
          <div className="font-display text-[20px] leading-none" style={{ color: "hsl(40 48% 88%)" }}>Kandake Prime</div>
          <div className="text-[11px] uppercase tracking-[0.18em] opacity-75 mt-1">GFMP · Internal Alpha</div>
        </div>
      </div>
      <Separator className="opacity-20" />
      <nav className="flex-1 py-3 px-2 space-y-1 overflow-auto">
        {NAV.filter((n) => !n.roles || n.roles.includes(user?.role)).map((n) => {
          const Icon = n.icon;
          const active = location.pathname.startsWith(n.to);
          return (
            <NavLink
              key={n.to}
              to={n.to}
              onClick={onNavigate}
              data-testid={`nav-${n.to.replace(/\//g, "").trim() || "dashboard"}`}
              className={`sidebar-nav-item flex items-center gap-3 px-3 py-2 rounded-md text-sm ${active ? "sidebar-nav-active" : ""}`}
            >
              <Icon className="w-4 h-4" />
              <span className="flex-1">{n.label}</span>
              {n.future && <span className="text-[9px] uppercase tracking-wide opacity-60">Soon</span>}
            </NavLink>
          );
        })}

        {/* GFMP Sync — operational grouping. Absent entirely when the
            authenticated role has no qualifying child entry. */}
        {gfmpItems.length > 0 && (
          <div className="mt-2">
            <button
              type="button"
              onClick={() => setGfmpOpen((value) => !value)}
              aria-expanded={gfmpOpen}
              aria-controls="gfmp-sync-panel"
              data-testid="nav-gfmp-sync-toggle"
              className="sidebar-nav-item w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm"
            >
              <Briefcase className="w-4 h-4" />
              <span className="flex-1 text-left">GFMP Sync</span>
              {gfmpOpen
                ? <ChevronDown className="w-3.5 h-3.5 opacity-70" />
                : <ChevronRight className="w-3.5 h-3.5 opacity-70" />}
            </button>
            {gfmpOpen && (
              <div
                id="gfmp-sync-panel"
                role="region"
                aria-label="GFMP Sync"
                className="mt-1 ml-2 pl-3 border-l border-white/10 space-y-1"
                data-testid="nav-gfmp-sync-panel"
              >
                {gfmpItems.map((item) => {
                  const Icon = item.icon;
                  const active = location.pathname.startsWith(item.to);
                  return (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      onClick={onNavigate}
                      title={item.description}
                      data-testid={`nav-gfmp-${item.to.split("/").pop()}`}
                      className={`sidebar-nav-item flex items-center gap-3 px-3 py-2 rounded-md text-[13px] ${active ? "sidebar-nav-active" : ""}`}
                    >
                      <Icon className="w-3.5 h-3.5" />
                      <span className="flex-1">
                        <span className="block">{item.label}</span>
                        {item.description && (
                          <span className="block text-[9px] leading-tight opacity-60">
                            {item.description}
                          </span>
                        )}
                      </span>
                    </NavLink>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* MAGI — distinct from Advanced Features. The entire section remains
            absent when the authenticated role has no qualifying MAGI entry. */}
        {magiItems.length > 0 && (
          <div className="mt-2">
            <button
              type="button"
              onClick={() => setMagiOpen((value) => !value)}
              aria-expanded={magiOpen}
              aria-controls="magi-panel"
              data-testid="nav-magi-toggle"
              className="sidebar-nav-item w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm"
            >
              <BookText className="w-4 h-4" />
              <span className="flex-1 text-left">
                <span className="block">MAGI</span>
                <span className="block text-[9px] leading-tight opacity-60">
                  AI-powered and AI-assisted intelligence systems
                </span>
              </span>
              {magiOpen
                ? <ChevronDown className="w-3.5 h-3.5 opacity-70" />
                : <ChevronRight className="w-3.5 h-3.5 opacity-70" />}
            </button>
            {magiOpen && (
              <div
                id="magi-panel"
                role="region"
                aria-label="MAGI"
                className="mt-1 ml-2 pl-3 border-l border-white/10 space-y-1"
                data-testid="nav-magi-panel"
              >
                {magiItems.map((item) => {
                  const Icon = item.icon;
                  const active = location.pathname.startsWith(item.to);
                  return (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      onClick={onNavigate}
                      title={item.description}
                      data-testid={`nav-magi-${item.to.split("/").pop()}`}
                      className={`sidebar-nav-item flex items-center gap-3 px-3 py-2 rounded-md text-[13px] ${active ? "sidebar-nav-active" : ""}`}
                    >
                      <Icon className="w-3.5 h-3.5" />
                      <span className="flex-1">
                        <span className="block">{item.label}</span>
                        <span className="block text-[9px] leading-tight opacity-60">
                          {item.description}
                        </span>
                      </span>
                    </NavLink>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* MP12 Advanced Features — collapsed section. Visible to any role
            that has at least one advanced item enabled. Standard flow stays
            uncluttered until the operator opens it. */}
        {advItems.length > 0 && (
          <div className="mt-2">
            <button
              type="button"
              onClick={() => setAdvOpen((v) => !v)}
              aria-expanded={advOpen}
              aria-controls="advanced-features-panel"
              data-testid="nav-advanced-features-toggle"
              className="sidebar-nav-item w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm"
            >
              <FlaskConical className="w-4 h-4" />
              <span className="flex-1 text-left">Advanced Features</span>
              {advOpen ? <ChevronDown className="w-3.5 h-3.5 opacity-70" /> : <ChevronRight className="w-3.5 h-3.5 opacity-70" />}
            </button>
            {advOpen && (
              <div
                id="advanced-features-panel"
                role="region"
                aria-label="Advanced Features"
                className="mt-1 ml-2 pl-3 border-l border-white/10 space-y-1"
                data-testid="nav-advanced-features-panel"
              >
                {advItems.map((n) => {
                  const Icon = n.icon;
                  const active = location.pathname.startsWith(n.to);
                  return (
                    <NavLink
                      key={n.to}
                      to={n.to}
                      onClick={onNavigate}
                      data-testid={`nav-adv-${n.to.split("/").pop()}`}
                      className={`sidebar-nav-item flex items-center gap-3 px-3 py-2 rounded-md text-[13px] ${active ? "sidebar-nav-active" : ""}`}
                    >
                      <Icon className="w-3.5 h-3.5" />
                      <span className="flex-1">{n.label}</span>
                    </NavLink>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {user?.role === "super_admin" && (
          <>
            <div className="px-3 pt-3 pb-1 text-[10px] uppercase tracking-[0.18em] opacity-50">Admin</div>
            {ADMIN_NAV.filter((n) => !n.role || n.role === user.role).map((n) => {
              const Icon = n.icon;
              const active = location.pathname.startsWith(n.to);
              return (
                <NavLink
                  key={n.to}
                  to={n.to}
                  onClick={onNavigate}
                  data-testid={`nav-${n.to.replace(/\//g, "-").replace(/^-/, "")}`}
                  className={`sidebar-nav-item flex items-center gap-3 px-3 py-2 rounded-md text-sm ${active ? "sidebar-nav-active" : ""}`}
                >
                  <Icon className="w-4 h-4" />
                  <span className="flex-1">{n.label}</span>
                </NavLink>
              );
            })}
            <NavLink
              to="/settings"
              onClick={onNavigate}
              data-testid="nav-settings"
              className={`sidebar-nav-item flex items-center gap-3 px-3 py-2 rounded-md text-sm ${location.pathname.startsWith("/settings") ? "sidebar-nav-active" : ""}`}
            >
              <SettingsIcon className="w-4 h-4" />
              <span>Settings / Admin</span>
            </NavLink>
          </>
        )}
      </nav>
      <div className="p-3 border-t border-white/10">
        <div className="flex items-center gap-3 px-2 py-2">
          <Avatar className="w-9 h-9">
            <AvatarFallback style={{ background: "hsl(40 48% 58%)", color: "hsl(225 14% 9%)" }}>
              {((user?.name || user?.email || "?").split(/[\s@.]+/).filter(Boolean).slice(0,2).map(s=>s[0]?.toUpperCase()).join("")) || "?"}
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0">
            <div className="text-sm truncate" data-testid="shell-user-name">{user?.name}</div>
            <div className="text-[11px] opacity-70 truncate">{roleLabel(user?.role)}</div>
          </div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="w-full justify-start text-white/80 hover:text-white"
          onClick={() => { onNavigate?.(); navigateTo("/account/profile"); }}
          data-testid="shell-my-profile-button"
        >
          <UserIcon className="w-4 h-4 mr-2" /> My profile
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="w-full justify-start text-white/80 hover:text-white"
          onClick={() => { onNavigate?.(); navigateTo("/account/security"); }}
          data-testid="shell-change-password-button"
        >
          <KeyRound className="w-4 h-4 mr-2" /> Change password
        </Button>
        <Button variant="ghost" size="sm" className="w-full justify-start text-white/80 hover:text-white" onClick={logout} data-testid="shell-logout-button">
          <LogOut className="w-4 h-4 mr-2" /> Sign out
        </Button>
        <div className="inline-flex items-center gap-2 mt-2 px-2 opacity-80" data-testid="shell-powered-by">
          <BrandLogo variant="kandake-gold" size="xs" ariaHidden />
          <span className="text-[10px] uppercase tracking-[0.18em]" style={{ color: "hsl(40 48% 88%)" }}>Powered by Kandake</span>
        </div>
      </div>
    </>
  );
}

function NotificationButton({ alerts }) {
  const total = (alerts?.missing_docs || 0) + (alerts?.attorney_review || 0) + (alerts?.draft_submissions || 0);
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative" data-testid="shell-notification-button" aria-label="Alerts">
          <Bell className="w-4 h-4" />
          {total > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full text-[10px] font-medium grid place-items-center" style={{background:"hsl(347 72% 22%)", color:"#fff"}} data-testid="shell-notification-count">{total}</span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[300px] p-0">
        <div className="p-3 border-b">
          <div className="text-sm font-medium">Priority alerts</div>
          <div className="text-[11px] text-muted-foreground">Open the Dashboard for full context.</div>
        </div>
        <ul className="text-sm divide-y">
          <li className="px-3 py-2 flex justify-between"><span>Tracks missing documents</span><span className="font-medium">{alerts?.missing_docs ?? "—"}</span></li>
          <li className="px-3 py-2 flex justify-between"><span>Attorney Review Required</span><span className="font-medium">{alerts?.attorney_review ?? "—"}</span></li>
          <li className="px-3 py-2 flex justify-between"><span>Draft submissions</span><span className="font-medium">{alerts?.draft_submissions ?? "—"}</span></li>
          <li className="px-3 py-2 flex justify-between"><span>Open license requests</span><span className="font-medium">{alerts?.open_requests ?? "—"}</span></li>
        </ul>
      </PopoverContent>
    </Popover>
  );
}

export default function AppShell() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchQ, setSearchQ] = useState("");
  const [alerts, setAlerts] = useState({});

  useEffect(() => {
    let active = true;
    api.get("/dashboard").then(r => {
      if (!active) return;
      const c = r.data?.counts || {};
      setAlerts({
        missing_docs: c.tracks_missing_documents,
        attorney_review: c.attorney_review_required,
        draft_submissions: c.draft_submissions,
        open_requests: c.open_license_requests,
      });
    }).catch(()=>{});
    return () => { active = false; };
  }, [location.pathname]);

  function submitSearch(e) {
    e.preventDefault();
    const q = searchQ.trim();
    navigate(q ? `/tracks?q=${encodeURIComponent(q)}` : "/tracks");
  }

  const internalBadge = useMemo(() => (
    <span className="hidden sm:inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-[10px] uppercase tracking-[0.16em] font-medium"
      style={{ background: "hsl(40 48% 92%)", color: "hsl(347 72% 22%)", border: "1px solid hsl(40 48% 78%)" }}
      data-testid="internal-alpha-badge"
    >
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: "hsl(40 48% 45%)" }} /> Internal Alpha
    </span>
  ), []);

  return (
    <div className="min-h-screen flex" data-testid="app-shell">
      {/* Desktop sidebar */}
      <aside
        className="w-[260px] shrink-0 hidden md:flex flex-col h-screen sticky top-0"
        style={{ background: "hsl(var(--sidebar))", color: "hsl(var(--sidebar-foreground))" }}
      >
        <SidebarContent user={user} location={location} onNavigate={()=>{}} logout={logout} />
      </aside>

      {/* Mobile sidebar drawer */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-50" data-testid="mobile-nav-drawer">
          <div className="absolute inset-0 bg-black/60" onClick={()=>setMobileOpen(false)} />
          <aside className="absolute top-0 left-0 bottom-0 w-[270px] flex flex-col" style={{ background: "hsl(var(--sidebar))", color: "hsl(var(--sidebar-foreground))" }}>
            <SidebarContent user={user} location={location} onNavigate={()=>setMobileOpen(false)} logout={logout} />
          </aside>
        </div>
      )}

      {/* Main column */}
      <div className="flex-1 min-w-0 flex flex-col bg-paper">
        {/* Live Beta notice — appears once per browser, dismissable */}
        <BetaNoticeBanner />
        {/* Top header */}
        <header className="sticky top-0 z-30 backdrop-blur supports-[backdrop-filter]:bg-background/80 bg-background border-b" data-testid="app-header">
          <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8 h-14 md:h-16 flex items-center gap-3">
            <Button variant="ghost" size="icon" className="md:hidden" onClick={()=>setMobileOpen(true)} aria-label="Open menu" data-testid="shell-mobile-menu-button">
              <Menu className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-2 min-w-0">
              <div className="md:hidden" data-testid="shell-mobile-kandake-logo">
                <BrandLogo variant="kandake-gold" size="sm" frame="rounded" />
              </div>
              <div className="min-w-0">
                <div className="font-display text-base md:text-lg leading-tight truncate" data-testid="shell-brand-title">Kandake Prime</div>
                <div className="hidden md:block text-[10px] uppercase tracking-[0.18em] text-muted-foreground -mt-0.5" data-testid="shell-brand-subtitle">GFMP Command Center · Rights-Readiness</div>
              </div>
              <span className="ml-2">{internalBadge}</span>
            </div>

            <form onSubmit={submitSearch} className="hidden md:flex flex-1 max-w-md mx-auto relative" data-testid="shell-search-form">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search catalog (title, artist, ISRC, catalog code)…"
                value={searchQ}
                onChange={(e) => setSearchQ(e.target.value)}
                className="pl-9 h-9 bg-secondary/60"
                data-testid="shell-search-input"
              />
            </form>

            <div className="ml-auto flex items-center gap-2">
              <NotificationButton alerts={alerts} />
              <div className="hidden sm:flex flex-col items-end leading-tight" data-testid="shell-user-pair">
                <span className="text-xs font-medium truncate max-w-[180px]">{user?.name}</span>
                <span className="text-[10px] uppercase tracking-wide text-muted-foreground">{roleLabel(user?.role)}</span>
              </div>
              <Avatar className="w-8 h-8">
                <AvatarFallback style={{ background: "hsl(347 72% 22%)", color: "hsl(40 48% 70%)" }}>
                  {((user?.name || user?.email || "?").split(/[\s@.]+/).filter(Boolean).slice(0,2).map(s=>s[0]?.toUpperCase()).join("")) || "?"}
                </AvatarFallback>
              </Avatar>
              <Button variant="ghost" size="icon" className="hidden md:inline-flex" onClick={logout} aria-label="Sign out" data-testid="shell-logout-icon">
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </header>

        <main className="flex-1">
          <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-8">
            <Outlet />
          </div>
        </main>

        <footer className="border-t mt-8">
          <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8 py-4 text-[11px] text-muted-foreground flex flex-wrap gap-2 justify-between items-center">
            <span>Kandake Prime · The Durban 49 Group / GOOD FRIAR MUSIC PUBLISHING · Internal use only.</span>
            <span className="uppercase tracking-wide">Compliance support only · Not legal advice · Human review required before external submission.</span>
            <span className="inline-flex items-center gap-2 uppercase tracking-[0.18em] text-[10px]" data-testid="app-footer-powered-by">
              <BrandLogo variant="kandake" size="xs" ariaHidden />
              Powered by <span className="font-medium" style={{ color: "hsl(347 72% 22%)" }}>Kandake</span>
            </span>
          </div>
        </footer>
      </div>
      {/* Floating feedback widget — visible on every authenticated page */}
      <FeedbackWidget />
    </div>
  );
}
